import { filter, omit } from "lodash";
import { useInjection } from "@linkfluence/core/di";
import { useQuery } from "@tanstack/react-query";
import {
  SearchRepository,
  SearchRepositoryUID,
} from "@radarly/core/search/repositories/SearchRepository";
import { useIsCurrentInsightPageGlobal } from "@radarly/models/insight-pages/is";
import {
  AIFragment,
  SearchFragment,
  SearchParams,
} from "@radarly/models/search";

const formatFragmentRequest = (
  fragment: SearchFragment | AIFragment,
  globalPageContext: boolean
): SearchParams => {
  const cleanRequest = omit(fragment.request, "name");

  if (globalPageContext) {
    const cleanClause = filter(
      fragment.request.query.and,
      (field) => Object.keys(field).toString() !== "dashboard"
    );
    return { ...cleanRequest, query: { and: cleanClause } };
  }
  return cleanRequest;
};

export function useSearchFragmentData<F extends SearchFragment | AIFragment>(
  projectId: number,
  fragment: F,
  mutualized: boolean = true
): {
  loading: boolean;
  isError: boolean;
  data: F["data"];
} {
  const globalPageContext = useIsCurrentInsightPageGlobal();
  const repository = useInjection<SearchRepository>(SearchRepositoryUID);
  const { data, isLoading, isError } = useQuery(
    ["/search/v3", projectId, fragment?.request],
    () => {
      return repository.searchFragmentData(
        projectId,
        formatFragmentRequest(fragment, globalPageContext),
        mutualized
      );
    },
    {
      cacheTime: 0,
      enabled: !!fragment,
    }
  );

  return {
    data,
    loading: !!fragment && isLoading,
    isError,
  };
}
